import * as React from 'react';
import { Select, type SelectOption, type SelectProps } from '@/components/ui/select';
import { formatRut } from '@/lib/rut';
import {
  COMPANY_LABEL_SEPARATOR,
  companyDisplayName,
  companyNameForDisplay,
} from './company-options';
import { EnvironmentBadge } from './environment-badge';
import {
  COMPANY_ENVIRONMENT_LABELS,
  deriveCompanyEnvironment,
  type CompanyEnvironment,
  type CompanyOption,
} from './company-environment';

/**
 * Contract: `docs/FRONTEND_COMPONENT_INVENTORY.md` §5.2, views 7 and 9.
 *
 * A typed wrapper over `components/ui/select.tsx`. It goes in `features/`, not
 * in `components/ui/`, because it knows `CompanyOption` (§3, convention 4 of
 * the inventory). It does NOT fetch: the list arrives as a prop.
 *
 * It is a defence against issuing under the wrong company, not just any
 * selector: two companies in one group have near-identical razones sociales,
 * and the RUT is the only thing that tells them apart unambiguously. The
 * environment (production/certification/sandbox) is the second line of defence
 * after `AppShell`'s environment badge — issuing from a certification company
 * and from a production one are different acts.
 *
 * The environment vocabulary (`CompanyEnvironment`, `CompanyOption`,
 * `deriveCompanyEnvironment`, `COMPANY_ENVIRONMENT_LABELS`) lives in
 * `company-environment.ts`: it is consumed by the badge, the layout and two
 * more screens, none of which needs this component in order to ask which
 * environment a company is in.
 */

/** The TYPES are re-exported so as not to break whoever already imported them
 *  from here; an `export type` is erased at compile time, so it does not trip
 *  `react-refresh/only-export-components` again. The values have to be
 *  imported from `./company-environment`. */
export type { CompanyEnvironment, CompanyOption };

/**
 * The sentinel for "all companies" in the screens whose own list filter can
 * widen past the header company (`usePageCompany({ allowAll: true })`).
 * `CompanySelect` itself no longer offers it: the in-page company filters it
 * used to back were removed in favour of the header selector. Real `id`s are
 * UUIDs (`gen_random_uuid()` in the database), so this literal can never
 * collide with a real one.
 */
export const COMPANY_ALL = 'all' as const;
export type CompanyIdOrAll = string | typeof COMPANY_ALL;

interface CompanySelectBaseProps {
  companies: readonly CompanyOption[];
  /** Restricts the set: real issuing (view 9) does not offer sandbox. */
  allowed?: readonly CompanyEnvironment[];
  disabled?: boolean;
  id?: string;
  /**
   * External id(s) describing the field (the `description` and the Zod error
   * `FormField` builds). They are FORWARDED to `Select`, which combines them
   * with the trigger's internal `aria-describedby` rather than replacing it.
   * They may arrive as a string (`"id1 id2"`) or as an array of ids.
   *
   * It exists because `FormField` injects these props into the child via
   * `cloneElement`: a child that does not declare them loses them silently —
   * neither the render nor the typecheck fails — and the error stays visible on
   * screen but unbound to the control, so a screen reader does not announce it
   * on focusing the field.
   */
  'aria-describedby'?: string | readonly string[];
  /** The field's invalid state, decided by `FormField`. It is forwarded to
   *  `Select`, which carries it to the `<button role="combobox">`. */
  'aria-invalid'?: boolean;
  /**
   * The accessible name, for when there is NO visible label naming the
   * control.
   *
   * In a form (views 7 and 9) it is unused: `FormField` puts a real `<label>`
   * there. It is needed in `AppShell`'s STRIP, where this selector hangs on
   * its own: a `combobox` does not take its name from its content, so without
   * this it was announced as «cuadro combinado, 99632700-0 · 99.632.700-0 ·
   * Sandbox» — the value, never saying what it is of. It is FORWARDED to
   * `Select`, like the other two ARIA props.
   */
  'aria-label'?: string;
  /**
   * `true` when the environment is ALREADY in view right beside the control:
   * `AppShell`'s strip paints the active company's `EnvironmentBadge`
   * immediately to the left, so the closed field's label said «Sandbox» twice
   * within 8px of itself.
   *
   * It omits the environment ONLY from the closed value, never from the open
   * list, and that asymmetry is what matters: the badge beside it speaks of the
   * ACTIVE company, but the options are other companies and each can be in a
   * different environment. A dropdown where a certification company cannot be
   * told from a production one is exactly the confusion this component exists
   * to prevent.
   *
   * And on a phone it is not merely cosmetic: the label is what gets truncated
   * when the width does not reach, and «· Sandbox» is ~70px spent repeating
   * what is already said, at the cost of the razón social and the RUT — which
   * is what tells two companies of one group apart.
   */
  environmentAlreadyVisible?: boolean;
  /**
   * How the control looks. Forwarded to `Select` as is — see its doc comment
   * for what each value paints. Added for `AppShell`'s header strip
   * (`variant="header"`), which needs the ghost trigger: at `h-9 min-h-touch`
   * (48px) a `field` border coincides exactly with the header's own top and
   * bottom edges (`AppLayout`'s `company` slot, `app-shell.tsx`'s
   * `h-row` header). Forms keep the default (`field`); no screen needs
   * `inline` through this wrapper today, but the type is not narrowed so as
   * not to fight `Select`'s own contract.
   *
   * With `variant="header"` the closed value is rendered as two parts: the
   * razón social and the `· 76.304.364-9` tail. From `sm` up the name is in full
   * (`shrink-0`) and the tail is the only part that truncates; below `sm` the
   * name may truncate too, but only once the tail is gone (see the render).
   * Other variants render the single-string label as before.
   */
  variant?: SelectProps<string>['variant'];
}

export interface CompanySelectProps extends CompanySelectBaseProps {
  value: string | null;
  onChange: (companyId: string) => void;
}

/**
 * A company's option: «razón social · RUT» as the label, and its environment as
 * the same `EnvironmentBadge` the header strip paints, at the row's end.
 *
 * The environment used to be a third text part of the label («… · Certificación»),
 * which put two vocabularies for one fact side by side: a badge with an icon in
 * the strip, plain text in the list right below it. As a badge each environment
 * keeps its own silhouette in the list too, so a scan can look for "the flask"
 * or "the shield" instead of reading every row's tail.
 *
 * The RUT stays IN the label and not in `description`: `description` is only
 * visible with the list OPEN (`components/ui/select.tsx`, `SelectItemRow`),
 * whereas `SelectPrimitive.Value` — what is seen in the already-CLOSED field —
 * renders the `label` only, and the RUT is what someone looks back at to
 * confirm which company ended up chosen before issuing. The badge is not lost
 * when the list closes either: `renderValue` below paints it in the closed
 * value unless `environmentAlreadyVisible` says it is already beside it.
 */
function companyOption(company: CompanyOption, disabled = false): SelectOption<string> {
  return {
    value: company.id,
    label: companyDisplayName(company),
    // The same two weights as the header strip: the razón social is what the
    // list is scanned by, the RUT the detail that settles a near-tie. The text
    // content still reads exactly as `label`, which remains the accessible name
    // and the typeahead text.
    content: (
      <>
        <span className="font-medium">{companyNameForDisplay(company)}</span>
        <span className="text-fg-muted">
          {COMPANY_LABEL_SEPARATOR}
          {formatRut(company.taxNumber, 'display')}
        </span>
      </>
    ),
    // `plain`, not the filled badge: the active row is highlighted, and a
    // filled badge on that highlight was two tints stacked. `w-28` makes the
    // environments one column whose left edges line up, whatever the word's
    // length («Certificación» is the longest).
    badge: (
      <EnvironmentBadge
        environment={deriveCompanyEnvironment(company)}
        appearance="plain"
        className="w-28"
      />
    ),
    ...(disabled ? { disabled: true } : {}),
  };
}

/**
 * A typed company selector, over `components/ui/select.tsx`.
 *
 * `allowed` restricts by environment but preserves `companies`'s order exactly
 * as it arrives in the props (it does not reorder, just like `DteTypeSelect`).
 * If the current `value` points at a company `allowed` left out, the option is
 * NOT hidden: it is added anyway, disabled, under its full real label — a
 * company selector that goes blank for a value that does have an owner is
 * exactly the kind of confusion this component exists to prevent.
 */
export function CompanySelect({
  companies,
  allowed,
  disabled,
  id,
  value,
  onChange,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  environmentAlreadyVisible,
  variant,
}: CompanySelectProps): React.JSX.Element {
  const permittedEnvironments = allowed;
  const filteredCompanies = permittedEnvironments
    ? companies.filter((company) =>
        permittedEnvironments.includes(deriveCompanyEnvironment(company)),
      )
    : companies;

  const options: SelectOption<string>[] = [];
  for (const company of filteredCompanies) {
    options.push(companyOption(company));
  }

  if (value !== null && !filteredCompanies.some((c) => c.id === value)) {
    const stray = companies.find((c) => c.id === value);
    if (stray) {
      options.push(companyOption(stray, true));
    }
  }

  return (
    <Select<string>
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
      id={id}
      variant={variant}
      // The header's two-part value and the form value with its badge decide
      // themselves which part truncates; only a plain-label value, which no
      // longer exists here, would want the wrapper's own ellipsis.
      valueOverflow="custom"
      // Forwarded explicitly: `Select` accepts them and combines its own
      // `aria-describedby` with them before putting them on the trigger.
      aria-describedby={describedBy}
      aria-invalid={ariaInvalid}
      aria-label={ariaLabel}
      renderValue={(option) => {
        if (!option) return undefined;
        const company = companies.find((c) => c.id === option.value);
        if (!company) return option.label;
        const environment = deriveCompanyEnvironment(company);
        const rut = formatRut(company.taxNumber, 'display');

        if (variant === 'header') {
          // Header strip: the RUT tail gives way first (U14). From `sm` up
          // the NAME never truncates (`sm:shrink-0`). Below `sm` a long
          // razón social alone can be wider than a phone, and a `shrink-0`
          // name made the whole page scroll sideways (`scrollWidth` 570 at
          // 390 px), so there the name may truncate too, as a last resort:
          // the tail's `shrink-1000` makes it absorb nearly all of the
          // shortfall before the name loses a character, and the `title`
          // keeps the full name one hover or long-press away.
          //
          // Weights: the razón social is the identity, so it carries
          // `font-medium` and the full foreground; the RUT is the detail that
          // disambiguates it, so it steps back to `fg-muted`. Before, both
          // were one regular-weight run and the company — the thing that
          // decides under whose RUT a document is issued — was lighter than
          // «Cerrar sesión» at the other end of the strip.
          //
          // The tail opens with a NO-BREAK space, not a plain one: a flex
          // item's leading white space is collapsed away, so a plain
          // «␠·␠RUT» tail rendered as «TCIT SpA· RUT» with the separator
          // glued to the name.
          const separator = COMPANY_LABEL_SEPARATOR.replace(/^ /, '\u00a0');
          const tail =
            environmentAlreadyVisible === true
              ? `${separator}${rut}`
              : `${separator}${rut}${COMPANY_LABEL_SEPARATOR}${COMPANY_ENVIRONMENT_LABELS[environment]}`;
          return (
            <span className="flex min-w-0">
              <span
                data-company-name
                title={company.legalName}
                className="min-w-0 truncate font-medium sm:shrink-0 sm:overflow-visible"
              >
                {companyNameForDisplay(company)}
              </span>
              <span data-company-rut className="min-w-0 shrink-1000 truncate text-fg-muted">
                {tail}
              </span>
            </span>
          );
        }

        // Every other variant: the label, plus the same badge the open list
        // shows — unless the environment is already in view beside the control.
        // The label truncates; the badge never does, it is the shorter and the
        // more consequential of the two.
        return (
          <span className="flex min-w-0 items-center gap-inset-sm">
            <span className="min-w-0 truncate">{companyDisplayName(company)}</span>
            {environmentAlreadyVisible === true ? null : (
              <EnvironmentBadge environment={environment} className="shrink-0" />
            )}
          </span>
        );
      }}
      placeholder="Selecciona una empresa"
    />
  );
}
